import { css, SerializedStyles } from '@emotion/react';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import { theme } from '@frontend/theme';

export const SIDE_PADDING = 5;
export const ADDED_WIDTH = SIDE_PADDING * 2;
export const TOP_BAR_HEIGHT = 72;

export const SEARCH_ENTITIES = [
  Entity.ENTITY_FEATURE,
  Entity.ENTITY_PERSON,
  Entity.ENTITY_SETTING,
  Entity.ENTITY_SMS,
] as const;

export const staticCss = {
  containerBase: css`
    flex-shrink: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,
  containerLarge: css`
    flex-grow: 1;
    max-width: 518px;
  `,
  containerXSmall: css`
    height: 100%;
  `,
  containerAlignEnd: css`
    align-items: flex-end;
  `,
  containerMarginLeftAuto: css`
    margin-left: auto;
  `,
  containerMarginRightAuto: css`
    margin-right: auto;
  `,
  dialogSearchBase: css`
    height: 48px;
    width: 100%;
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    box-shadow: none !important;
    outline: none;
  `,
  dialogSearchShellBase: css`
    height: 34px;
    width: 100%;
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    box-shadow: none !important;
    outline: none;
  `,
  dialogSearchContainer: css`
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    transition: 0.2s linear;
    caret-color: ${theme.colors.neutral50};

    :focus-within {
      background: ${theme.colors.neutral10};
    }

    & input {
      color: ${theme.colors.neutral90};
    }
    & input::placeholder {
      color: ${theme.colors.neutral50};
    }
  `,
  dialogSearchContainerDefault: css`
    button {
      width: 40px;
      height: 40px;
    }
    svg {
      width: 24px;
      height: 24px;
      color: ${theme.colors.neutral50};
    }
  `,
  dialogSearchContainerShell: css`
    button {
      width: 30px;
      height: 30px;
    }
    svg {
      width: 20px;
      height: 20px;
      color: ${theme.colors.neutral50};
    }
  `,
  searchFieldDefault: css`
    height: 48px;
    width: 100%;
    max-width: 518px;
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    :focus {
      border: 1px solid ${theme.colors.primary20};
    }
    transition: 0.3s ease;
  `,
  searchFieldShell: css`
    height: 34px;
    width: 100%;
    max-width: 518px;
    outline: none;
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    :focus {
      border: 1px solid ${theme.colors.primary20};
    }
    transition: 0.3s ease;
  `,
  opacityHidden: css`
    opacity: 0;
  `,
  buttonDefault: css`
    height: 100%;
    min-width: 56px;
    border-radius: 0;
    width: 56px;
    :hover {
      background: ${theme.colors.neutral5};
    }
  `,
  dialogInnerFlex: css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  `,
  dialogResultsMargin: css`
    margin: 0 -5px -9px -5px;
  `,
  allResultsWrapper: css`
    margin: 0 -5px -8px -5px;
    border-top: 1px solid ${theme.colors.neutral20};
  `,
  allResultsButton: css`
    padding: ${theme.spacing(2)};
    width: 100%;
    color: ${theme.font.colors.primary};
    font-size: ${theme.font.size.medium};
    font-weight: ${theme.font.weight.bold};
    :hover {
      background-color: ${theme.colors.neutral5};
    }
  `,
  searchFieldContainerDefault: css`
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    transition: background-color 0.2s ease;
    caret-color: ${theme.colors.neutral50};
    opacity: 1;

    & input {
      color: ${theme.colors.neutral90};
    }
    & input::placeholder {
      color: ${theme.colors.neutral50} !important;
    }
    button {
      width: 40px;
      height: 40px;
    }
    svg {
      width: 24px;
      height: 24px;
      color: ${theme.colors.neutral50};
    }
  `,
  searchFieldContainerDefaultOpen: css`
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    transition: background-color 0.2s ease;
    caret-color: ${theme.colors.neutral50};

    & input {
      color: ${theme.colors.neutral90};
    }
    & input::placeholder {
      color: ${theme.colors.neutral50} !important;
    }
    button {
      width: 40px;
      height: 40px;
    }
    svg {
      width: 24px;
      height: 24px;
      color: ${theme.colors.neutral50};
    }
    background-color: ${theme.colors.neutral10};
    opacity: 0;
  `,
  shellSearchFieldContainer: css`
    background: transparent;
    border-radius: 0;
    transition: background-color 0.2s ease;
    caret-color: ${theme.colors.white};
    opacity: 1;
    -webkit-app-region: no-drag;

    & input {
      color: ${theme.colors.neutral90};
    }
    button {
      width: 30px;
      height: 30px;
    }
    svg {
      width: 20px;
      height: 20px;
    }
  `,
  shellSearchFieldContainerOpen: css`
    background: transparent;
    border-radius: 0;
    transition: background-color 0.2s ease;
    caret-color: ${theme.colors.white};
    -webkit-app-region: no-drag;

    & input {
      color: ${theme.colors.neutral90};
    }
    button {
      width: 30px;
      height: 30px;
    }
    svg {
      width: 20px;
      height: 20px;
    }
    background-color: ${theme.colors.neutral10};
    opacity: 0;
  `,
  dialogBaseDefault: css`
    padding: 8px 5px 16px 5px;
    border-radius: 16px;
    box-shadow: ${theme.shadows.light};
    overflow: hidden;
    contain: layout style;
  `,
  dialogBaseShell: css`
    box-shadow: ${theme.shadows.light};
    overflow: hidden;
    padding: 4px 5px 16px 5px;
    border-radius: 8px;
    max-height: calc(100vh - 10px);
    -webkit-app-region: no-drag;
    contain: layout style;
  `,
  dialogExpanded: css`
    width: 100%;
    height: 100%;
  `,
  dialogExpandedShell: css`
    width: 100%;
    height: 95%;
  `,
  formMaxWidthDefault: css`
    form {
      flex: 1;
      max-width: 518px;
      transition: 0.3s ease;
    }
  `,
  formMaxWidthExpanded: css`
    form {
      flex: 1;
      max-width: 748px;
      transition: 0.3s ease;
    }
  `,
  resultsContainerDefault: css`
    max-width: 518px;
    transition: 0.3s ease;
    margin: auto;
    display: flex;
    flex-direction: column;
  `,
  resultsContainerExpanded: css`
    max-width: 748px;
    transition: 0.3s ease;
    margin: auto;
    display: flex;
    flex-direction: column;
  `,
  closeButtonAbsolute: css`
    position: absolute;
    top: 12px;
    right: 8px;
  `,
  shellBtnBaseStatic: css`
    border-radius: ${theme.borderRadius.small};
    min-width: 32px;
    width: 32px;
    -webkit-app-region: no-drag;
    background: transparent !important;
  `,
  windowsBtnCss: css`
    margin-right: auto;
    margin-left: 0;
  `,
} as const;

export const BUTTON_DEFAULT_CLOSED = [staticCss.buttonDefault] as const as SerializedStyles[];
export const BUTTON_DEFAULT_OPEN = [staticCss.buttonDefault] as const as SerializedStyles[];
export const SEARCH_FIELD_DEFAULT_CLOSED = [staticCss.searchFieldDefault] as const as SerializedStyles[];
export const SEARCH_FIELD_DEFAULT_OPEN = [
  staticCss.searchFieldDefault,
  staticCss.opacityHidden,
] as const as SerializedStyles[];
export const DIALOG_SEARCH_DEFAULT = [staticCss.dialogSearchBase] as const as SerializedStyles[];
export const DIALOG_SEARCH_CONTAINER_DEFAULT = [
  staticCss.dialogSearchContainer,
  staticCss.dialogSearchContainerDefault,
] as const as SerializedStyles[];
export const DIALOG_SEARCH_CONTAINER_SHELL = [
  staticCss.dialogSearchContainer,
  staticCss.dialogSearchContainerShell,
] as const as SerializedStyles[];
export const DIALOG_SEARCH_SHELL_NO_MARGIN = [staticCss.dialogSearchShellBase] as const as SerializedStyles[];

export const DIALOG_TRANSITION = { duration: 0.3, ease: 'linear' as const } as const;
